<template>
    <div>
        <LoadingSection :loading="lesson === null" :error="error"></LoadingSection>
        <article v-if="lesson">
            <AppSubheader title="Video" :dark="true" :parent="parent">
                <ButtonSubheader
                    v-if="previousRoute"
                    :buttonRoute="previousRoute"
                    buttonIcon="IconChevronLeft"
                    buttonText="Prev"
                    :space="false"
                    :dark="true"
                ></ButtonSubheader>
                <ButtonSubheader
                    v-if="nextRoute"
                    :buttonRoute="nextRoute"
                    buttonIcon="IconChevronRight"
                    buttonText="Next"
                    :dark="true"
                    :space="false"
                    :flip="true"
                ></ButtonSubheader>
            </AppSubheader>
            <SectionLessonEmbed
                :lesson="lesson"
                @refresh="refresh"
            ></SectionLessonEmbed>
            <SectionTiles
                v-if="isLoggedIn && lesson.lesson_chords?.length"
                padding="pt-12 sm:pt-14"
                heading="Chord Sheets"
                headingComponent="h3"
                :tiles="lesson.lesson_chords.map((sheet) => {
                    return {
                        url: sheet.lesson_chord_url,
                        heading: sheet.lesson_chord_text,
                    };
                })"
            ></SectionTiles>
            <SectionLessonFiles
                :lesson="lesson"
            ></SectionLessonFiles>
            <SectionLessonDescription
                :lesson="lesson"
            ></SectionLessonDescription>
            <SectionLessonNavigation
                :lesson="lesson"
                :previousRoute="previousRoute"
                :nextRoute="nextRoute"
            ></SectionLessonNavigation>
            <SectionTiles
                v-if="lesson?.lesson_course?.course_lessons?.length"
                color="bg-zinc-100"
                heading="Course Outline"
                headingComponent="h3"
                :content="`<p>${lesson?.lesson_course.course_title}</p>`"
                :tiles="lesson?.lesson_course?.course_lessons.map((sibling) => {
                    return {
                        route: useContextLesson().route(sibling, 'course', lesson?.lesson_course?.course_slug),
                        heading: sibling.lesson_title,
                        headingComponent: 'span',
                        active: $route.params.lessonSlug === sibling.lesson_slug,
                        headingColor: 'text-white',
                    };
                })"
            ></SectionTiles>
            <div v-if="!lesson.lesson_course">
                <SectionImage
                    v-if="lesson.related_courses?.length"
                    color="bg-zinc-900"
                    heading="Enjoying this lesson?"
                    :heading-color="'text-white'"
                    heading-component="h3"
                    :content="lesson.related_courses[0].course_excerpt"
                    :content-color="'text-white'"
                    :image="$config.public.uploads + 'images-course/' + lesson.related_courses[0].course_image"
                    imageAlt="A thumbnail from an online course that you may find relevant"
                    :subheading="'Try this course - ' + lesson.related_courses[0].course_title"
                    :subheading-color="'text-white'"
                    subheading-component="h4"
                    button-text="Go To Course"
                    :button-route="'/courses/' + lesson.related_courses[0].course_slug"
                    buttonIcon="IconCourses"
                ></SectionImage>
                <SectionImage
                    v-else
                    color="bg-zinc-900"
                    heading="Enjoying this lesson?"
                    :heading-color="'text-white'"
                    heading-component="h3"
                    content="<p>Have you tried a course yet? There are many courses on this website covering all levels and styles of guitar, ukulele basics and even great guest teacher courses. Browse the courses now and let them take your playing to the next level.</P>"
                    :content-color="'text-white'"
                    :image="$config.public.uploads + 'images-general/course-collage-nov-24.jpg'"
                    imageAlt="A selection of courses provided on Andy Guitar"
                    button-text="Go to Courses"
                    :button-route="'/courses'"
                    buttonIcon="IconCourses"
                ></SectionImage>
            </div>
        </article>
    </div>
</template>

<script setup>
const {$config} = useNuxtApp();

const {isLoggedIn} = storeToRefs(useStoreAuth());
const {lesson, error, nextCourse} = storeToRefs(useStoreLesson());
const {cookies} = storeToRefs(useStoreCookies());

const lessonSlug = useRoute().params.lessonSlug;
const courseSlug = useRoute().query.courseSlug;
const pathwaySlug = useRoute().query.pathwaySlug;

const {
    getLesson,
    onViewLesson,
    onPlayVideo,
    onSeekVideo,
    onStopVideo,
    onYoutubePlayerLoaded,
    onSoundslicePlayAction,
} = useStoreLesson();

const parent = computed(() => {
    return useContextLesson().parent();
});

const previousRoute = computed(() => {
    return useContextLesson().routePrevious(lesson.value);
});

const nextRoute = computed(() => {
    return useContextLesson().routeNext(lesson.value, nextCourse.value?.course_slug);
});

const videoCookies = computed(() => {
    return !!cookies.value.video;
});

function refresh () {
    getLesson({lessonSlug, courseSlug, pathwaySlug});
}

function addSoundsliceEventListeners () {
    window.addEventListener('message', onPostMessageEvent); // Uses generic message event
}

function addNativeVideoPlayerEventListeners () {
    let players = document.querySelectorAll('.video-player');
    window.players = players;
    for (let i = 0; i < players.length; i++) {
        players[i].addEventListener('play', onPlayVideo);
        players[i].addEventListener('seeking', onSeekVideo);
        players[i].addEventListener('pause', onStopVideo);
    }
}

function addYoutubePlayerEventListeners () {
    function addListener (player) {
        player.addEventListener('youtube-player-loaded', onYoutubePlayerLoaded);
    }

    let players = document.querySelectorAll('.embed-youtube-lazy-load');
    players.forEach(player => addListener(player));
}

function onPostMessageEvent (event) {
    if (!event.origin.includes('soundslice.com')) return;
    let data = JSON.parse(event.data);
    if (!(data.method === 'ssPlay')) return;
    // Soundslice play event
    if (data.method === 'ssPlay') {
        onSoundslicePlayAction(event);
    }
}

function addEventListeners () {
    nextTick(() => {
        addYoutubePlayerEventListeners();
        addNativeVideoPlayerEventListeners();
        addSoundsliceEventListeners();
    });
}

watch(lesson, (newLesson, oldLesson) => {
    if (!newLesson?.lesson_slug) return;
    if (oldLesson?.lesson_slug === newLesson?.lesson_slug) return;
    return onViewLesson();
});

watch(videoCookies, (newValue, oldValue) => {
    if (newValue && !oldValue) nextTick(() => {
        addEventListeners();
    });
});

onUpdated(() => {
    addEventListeners();
});

onMounted(() => {
    addEventListeners();
    onViewLesson();
});

await useLazyAsyncData('load', () => useStoreLesson().getLesson({lessonSlug, courseSlug, pathwaySlug}).then(() => true));

useSeoLesson().buildReactiveHead(lesson, error);
</script>
